import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';

const colorPalette = ['#EF4444', '#F59E0B', '#F97316', '#FB923C', '#FDA4AF', '#FCA5A5'];

const InverterClippingChart = ({ startDate, endDate, assetIds = [] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [seriesKeys, setSeriesKeys] = useState([]);
  const [rows, setRows] = useState([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await apiService.getForEnergyLoss({
        start_date: `${startDate} 00:00:00`,
        end_date: `${endDate} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: assetIds.length ? assetIds : [API_CONFIG.PLANT_ID]
      });
      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      if (!Array.isArray(arr) || arr.length === 0) {
        setSeriesKeys([]);
        setRows([]);
        return;
      }
      const keysSet = new Set();
      arr.forEach(d => {
        const map = d?.readings?.[0]?.EYA?.inverter_clipping_loss_list_percentage || {};
        Object.entries(map).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') {
            keysSet.add(k);
          }
        });
      });
      const firstMap = arr[0]?.readings?.[0]?.EYA?.inverter_clipping_loss_list_percentage || {};
      const rowsArr = Object.entries(firstMap).map(([inv, val]) => ({ inverter: inv, value: Number(parseFloat(val).toFixed(2)) }));
      setSeriesKeys(['value']);
      setRows(rowsArr);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const bars = useMemo(() => seriesKeys.map((key, idx) => (
    <Bar key={key} dataKey={key} name={key.replace(/_wms/g, '').replace(/_/g, ' ')} fill={colorPalette[idx % colorPalette.length]} />
  )), [seriesKeys]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Inverter Clipping (%)</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : rows.length === 0 || seriesKeys.length === 0 ? (
          <div className="text-center text-gray-500">No Inverter Clipping data</div>
        ) : (
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 60, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="inverter" angle={-90} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name]} />
                <Legend />
                <Bar dataKey="value" name="Clipping (%)" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InverterClippingChart;


